import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import Maps from "../../components/Map";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import SearchAutocomplete from "../../components/SearchAutoComplete";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import clusterService from "../../services/cluster";
import queryDefault from "../../utils/queryDefault";
import iconService from "../../services/icon";

export default function NearbyEdit() {
  const { id } = useParams();
  const notification = useNotification();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);

  const [options] = useState<any>({
    keyword: "",
  });
  const [markerPosition, setMarkerPosition] =
    useState<google.maps.LatLngLiteral>({
      lat: -6.1753917, // Default initial latitude
      lng: 106.8271533, // Default initial longitude
    });

  const schema = yup.object().shape({
    name: yup
      .string()
      .min(2)
      .required("name is a required field and length > 2"),
  });
  const form = useForm({
    defaultValues: {
      name: "",
      username: user.username,
      cluster_id: "",
      map_name: "",
      location: { x: 0, y: 0 },
      icon: ""
    },
    resolver: yupResolver(schema),
  });

  const handleMarkerDragEnd = (lat: number, lng: number) => {
    setMarkerPosition({ lat, lng });
    form.setValue("location.x", lat);
    form.setValue("location.y", lng);
  };

  const handlePlaceSelected = (
    place: google.maps.places.PlaceResult | null
  ) => {
    if (place) {
      setMarkerPosition({
        lat: place.geometry?.location?.lat() ?? 0,
        lng: place.geometry?.location?.lng() ?? 0,
      });
      form.setValue("location", {
        x: place.geometry?.location?.lat() ?? 0,
        y: place.geometry?.location?.lng() ?? 0,
      });
      form.setValue("map_name", place?.name ?? form.getValues("map_name"));
    }
  };

  const updateNearby = useMutation(
    "update_nearby",
    clusterService.updateNearby,
    {
      onSuccess: () => {
        navigate(
          `/app/properties/cluster/detail/${form.getValues("cluster_id")}`
        );

        notification.onOpen({
          message: "Nearby was successfully updated!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const listCluster = useQuery(
    ["cluster_list", options],
    ({ queryKey }) => {
      return clusterService.getAllCluster({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  const currentNearby = useQuery("current-nearby", () =>
    clusterService.getNearbyById(Number(id))
  );

  const onSubmit = (values: any) => {
    updateNearby.mutate({ ...values, id });
  };

  const [filter, setFilter] = useState("");

  const getLogoList = useQuery(
    ["icons"],
    () => {
      return iconService.getAllIcon();
    },
    {
      ...queryDefault,
    }
  );

  useEffect(() => {
    if (listCluster.isFetched && currentNearby.isFetched) {
      form.setValue("cluster_id", currentNearby.data.data.cluster_id);
      form.setValue("username", user.username);
      form.setValue("name", currentNearby.data.data.name);
      form.setValue("location", currentNearby.data.data.location);
      form.setValue("map_name", currentNearby.data.data.map_name);
      setMarkerPosition({
        lat: get(currentNearby.data, "data.location.x"),
        lng: get(currentNearby.data, "data.location.y"),
      });
    }
  }, [
    form,
    id,
    user.username,
    listCluster.isFetched,
    currentNearby.isFetched,
    currentNearby.data,
  ]);

  return (
    <MainLayout>
      <Breadcrumb
        label="Edit Nearby"
        breadcrumbs={[
          { label: "Cluster", href: "/app/properties/cluster" },
          {
            label: "Cluster Detail",
            href: `/app/properties/cluster/nearby/detail/${form.getValues(
              "cluster_id"
            )}`,
          },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="cluster"
        requiredPermissions={{ is_create: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Cluster</FormLabel>
                <Controller
                  name="cluster_id"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                        disabled
                      >
                        {get(listCluster, ["data", "data"], []).map(
                          (item: any, i: any) => (
                            <MenuItem value={item?.id} key={item?.id}>
                              {item?.name}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Icon</FormLabel>
                <Controller
                  name="icon"
                  control={form.control}
                  render={({ field }) => (
                    <Autocomplete
                      options={get(getLogoList, "data.data", [])}
                      getOptionLabel={(option: any) => option.name}
                      onChange={(event, newValue) => {
                        form.setValue("icon", newValue ? newValue.name : "");
                      }}
                      filterOptions={(options, { inputValue }) =>
                        options.filter((option) =>
                          option.name
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Icon"
                          variant="outlined"
                          value={filter}
                          onChange={(e: any) => {
                            setFilter(e.target.value);
                            if (params.inputProps.onChange) {
                              params.inputProps.onChange(e); // Ensure the input value is managed correctly
                            }
                          }}
                        />
                      )}
                      noOptionsText="No icons available"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <SearchAutocomplete onPlaceSelected={handlePlaceSelected} />
              </FormControl>
              <Box
                sx={{
                  width: "100%",
                  height: "300px",
                }}
              >
                <Maps
                  center={markerPosition}
                  onMarkerDragEnd={handleMarkerDragEnd}
                  dragable={true}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={updateNearby.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
